// Progresión local: nivel, experiencia, monedas, estadísticas, misiones y logros.
import { SaveManager, defaultProfile } from './SaveManager.js';
import achievementsData from '../data/achievements.json';
import missionsData from '../data/missions.json';
import { EVENTS } from '../utils/constants.js';

export class ProgressionManager {
  constructor(bus, config) {
    this.bus = bus;
    this.cfg = config.progression;
    this.profile = SaveManager.loadProfile() || defaultProfile();
    this.achievements = achievementsData.achievements;
    this.missions = missionsData.daily;
    this._migrateAchievements();
    this._migrateLeaderboard();
    // Desbloqueo RETROACTIVO: al cargar (o al añadir logros nuevos), marca como
    // desbloqueados los que el jugador YA cumple por sus estadísticas (quedan pendientes de
    // canjear). Se ejecuta antes de enganchar los listeners → sin spam de avisos.
    this._checkAllAchievements();
  }

  /**
   * Perfiles antiguos otorgaban la recompensa del logro AL DESBLOQUEAR (sin canjear). Para
   * no regalarla dos veces, un logro ya desbloqueado sin campo `claimed` se marca como
   * canjeado. Los NUEVOS desbloqueos empiezan con `claimed:false` (hay que canjearlos).
   */
  _migrateAchievements() {
    let changed = false;
    for (const a of this.achievements) {
      const rec = this.profile.achievements[a.id];
      if (rec && rec.unlocked && rec.claimed === undefined) { rec.claimed = true; changed = true; }
    }
    if (changed) this.save();
  }

  save() {
    SaveManager.saveProfile(this.profile);
  }

  // --- XP / nivel ---
  xpForLevel(level) {
    return this.cfg.xpPerLevelBase + (level - 1) * this.cfg.xpPerLevelStep;
  }

  addXP(amount) {
    this.profile.xp += amount;
    let leveled = false;
    while (this.profile.xp >= this.xpForLevel(this.profile.level)) {
      this.profile.xp -= this.xpForLevel(this.profile.level);
      this.profile.level += 1;
      leveled = true;
    }
    this.bus.emit(EVENTS.XP_GAINED, { xp: this.profile.xp, level: this.profile.level, leveled });
    if (leveled) this._checkAllAchievements(); // logros de nivel
    this.save();
    return leveled;
  }

  addCoins(n) {
    this.profile.coins += n;
    this.profile.stats.coinsEarned = (this.profile.stats.coinsEarned || 0) + Math.max(0, n);
    this.save();
  }

  // --- Estadísticas ---
  incStat(stat, by = 1) {
    this.profile.stats[stat] = (this.profile.stats[stat] || 0) + by;
    this._checkAllAchievements();
  }

  setStatMax(stat, value) {
    if (!this.profile.stats[stat] || value > this.profile.stats[stat]) {
      this.profile.stats[stat] = value;
      this._checkAllAchievements(); // logros de récord (mejor puntuación, palabra más larga…)
    }
  }

  recordLongestWord(word) {
    if (!word) return;
    if ((this.profile.stats.longestWord || '').length < word.length) {
      this.profile.stats.longestWord = word;
    }
    // Longitud (en caracteres reales) para los logros de "palabras largas".
    this.setStatMax('longestWordLen', [...word].length);
  }

  /** Registra la palabra de mayor puntuación (por valor de sus fichas). */
  recordBestWord(word, points) {
    if (!word || !points) return;
    const cur = this.profile.stats.bestWord || { word: '', points: 0 };
    if (points > (cur.points || 0)) {
      this.profile.stats.bestWord = { word, points };
    }
  }

  addDiscoveredWords(words) {
    const set = new Set(this.profile.discoveredWords);
    for (const w of words) set.add(w.toLowerCase());
    this.profile.discoveredWords = [...set];
    this._checkAllAchievements(); // logros de "palabras distintas descubiertas"
  }

  // --- Logros ---
  /** Valor actual de la estadística de un logro (con casos especiales que no viven en stats). */
  _statValue(stat) {
    if (stat === 'level') return this.profile.level;
    if (stat === 'discoveredWords') return (this.profile.discoveredWords || []).length;
    return this.profile.stats[stat] || 0;
  }

  // Evalúa TODOS los logros contra las estadísticas actuales. Al alcanzar el objetivo el
  // logro se DESBLOQUEA pero NO otorga la recompensa: queda pendiente de CANJEAR por el
  // jugador (pin rojo en el menú). Ver claimAchievement().
  _checkAllAchievements() {
    for (const a of this.achievements) {
      const rec = this.profile.achievements[a.id] || { progress: 0, unlocked: false, claimed: false };
      rec.progress = this._statValue(a.stat);
      if (!rec.unlocked && rec.progress >= a.target) {
        rec.unlocked = true;
        rec.claimed = false; // pendiente de canjear
        this.bus.emit(EVENTS.ACHIEVEMENT_UNLOCKED, { achievement: a });
      }
      this.profile.achievements[a.id] = rec;
    }
    this.save();
  }

  /** Nº de logros DESBLOQUEADOS (conseguidos). Sirve para desbloquear modos de juego. */
  unlockedAchievementsCount() {
    let n = 0;
    for (const a of this.achievements) if (this.profile.achievements[a.id]?.unlocked) n++;
    return n;
  }

  /** ¿Está desbloqueado un logro concreto? (para desbloquear modos con un logro específico). */
  isAchievementUnlocked(id) {
    return !!this.profile.achievements[id]?.unlocked;
  }

  /** Definición de un logro por id (label, desc, target, stat…) o null. */
  getAchievement(id) {
    return this.achievements.find((a) => a.id === id) || null;
  }

  /** Progreso actual hacia un logro: { progress, target } (progress limitado al target). */
  achievementProgress(id) {
    const a = this.getAchievement(id);
    if (!a) return { progress: 0, target: 0 };
    const cur = this._statValue(a.stat);
    return { progress: Math.min(cur, a.target), target: a.target };
  }

  /** Nº de logros desbloqueados pendientes de CANJEAR (para el pin del menú). */
  claimableCount() {
    let n = 0;
    for (const a of this.achievements) {
      const rec = this.profile.achievements[a.id];
      if (rec && rec.unlocked && !rec.claimed) n++;
    }
    return n;
  }

  /** Canjea un logro desbloqueado: otorga XP + monedas UNA sola vez. Devuelve {xp,coins} o null. */
  claimAchievement(id) {
    const a = this.achievements.find((x) => x.id === id);
    if (!a) return null;
    const rec = this.profile.achievements[id];
    if (!rec || !rec.unlocked || rec.claimed) return null;
    rec.claimed = true;
    this.addXP(a.reward.xp); // addXP ya guarda. Los logros dan solo XP (sin monedas).
    return { xp: a.reward.xp };
  }

  /** Canjea TODOS los logros desbloqueados pendientes. Devuelve {count, xp} del total. */
  claimAllAchievements() {
    let xp = 0, count = 0;
    for (const a of this.achievements) {
      const rec = this.profile.achievements[a.id];
      if (rec && rec.unlocked && !rec.claimed) { rec.claimed = true; xp += a.reward.xp; count++; }
    }
    if (count > 0) this.addXP(xp); // una sola vez (guarda + posible subida de nivel)
    return { count, xp };
  }

  // --- Ranking (LOCAL por ahora; en el futuro se sincronizará con Firebase) ---
  /** Deduplica por JUGADOR conservando su MEJOR puntuación (una sola fila por jugador).
   *  Tú = clave fija; rivales = por nombre. Devuelve el top 50 ordenado. */
  _dedupeBestPerPlayer(entries) {
    const byKey = new Map();
    for (const e of entries || []) {
      if (!e || typeof e.score !== 'number') continue;
      const key = e.self ? '__self__' : `n:${String(e.name || '').toLowerCase()}`;
      const score = Math.round(e.score);
      const cur = byKey.get(key);
      if (!cur || score > cur.score) {
        byKey.set(key, { name: e.name || 'Jugador', avatar: e.avatar || '🙂', score, self: !!e.self, ts: e.ts ?? 0 });
      }
    }
    return [...byKey.values()].sort((a, b) => b.score - a.score).slice(0, 50);
  }

  /** Registra los resultados de una partida: SOLO la MEJOR puntuación de cada jugador.
   *  @param {Array<{name,avatar,score,self}>} entries  @param {number} ts */
  recordLeaderboard(entries, ts = 0) {
    const board = Array.isArray(this.profile.leaderboard) ? this.profile.leaderboard : [];
    const stamped = (entries || []).map((e) => ({ ...e, ts }));
    this.profile.leaderboard = this._dedupeBestPerPlayer([...board, ...stamped]);
    this.save();
  }

  /** Limpia rankings antiguos: deja una sola fila por jugador (su mejor puntuación). */
  _migrateLeaderboard() {
    const board = this.profile.leaderboard;
    if (!Array.isArray(board) || !board.length) return;
    this.profile.leaderboard = this._dedupeBestPerPlayer(board);
    this.save();
  }

  /** Ranking local ordenado (mejores primero). En el futuro: leer de Firebase. */
  getLeaderboard() {
    return [...(this.profile.leaderboard || [])].sort((a, b) => b.score - a.score);
  }

  // --- Racha / recompensa diaria ---
  claimDailyReward(todayStr) {
    const last = this.profile.stats.lastPlayedDate;
    if (last === todayStr) return { already: true };
    // Racha: +1 si ayer; reinicia si no.
    this.profile.stats.dailyStreak = (this.profile.stats.dailyStreak || 0) + 1;
    this.profile.stats.lastPlayedDate = todayStr;
    const coins = this.cfg.dailyRewardCoins;
    this.addCoins(coins);
    this._checkAllAchievements();
    this.save();
    return { already: false, coins, streak: this.profile.stats.dailyStreak };
  }

  // --- Resultado de partida ---
  applyGameResult({ won, score, longestWord, evolutions = 0, familiesCompleted = 0, wordsCreated = 0, tripleTurn = false }) {
    this.incStat('gamesPlayed');
    if (won) this.incStat('wins');
    this.incStat('evolutions', evolutions);
    this.incStat('familiesCompleted', familiesCompleted);
    this.incStat('wordsCreated', wordsCreated);
    if (tripleTurn) this.incStat('tripleTurns');
    this.recordLongestWord(longestWord);
    this.setStatMax('bestScore', score);
    const xp = Math.round(score / 2) + (won ? 50 : 15);
    this.addXP(xp);
    this.addCoins(won ? 40 : 15);
    return xp;
  }
}
